/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext } from 'react';
import { compact, defaultTo, head, isEmpty, map } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { FormApi } from 'final-form';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import {
  BrandColors,
  LinkButton,
  LinkButtonWrapper,
  LoneIconButton,
  Mixins,
  PopoverList,
  Upload,
  uploadSingleFile,
} from '@blend/enterprise-components';

import { uploadFile } from 'blb-api/files';
import { addDocsToLending } from 'blb-api/loans';
import { openUrlInNewTab } from 'blb-utils/misc';

import { TitleContext } from './TitleTab';
import {
  creditorNameInputConfig,
  docInputConfig,
  lienAmountInputConfig,
  lienStatusInputConfig,
  lienTypeInputConfig,
  requirementDescriptionInputConfig,
  requirementStatusInputConfig,
  taxAmountInputConfig,
  taxDueDateInputConfig,
  taxNameInputConfig,
  taxStatusInputConfig,
} from './inputConfigs';
import { Curative, FormValues } from './types';
import { getPercentagesFromWidths } from './utils';

const styles = StyleSheet.create({
  curativeAction: {
    marginRight: 10,
  },
  lenderTableBodyTr: {
    ':not(:last-child)': {
      borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
    },
  },
  lenderTableHeaderTr: {
    borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
  },
  lenderTableRoot: {
    tableLayout: 'fixed',
    width: '100%',
  },
  lenderTableStrictWidth: {
    display: 'inline-block',
  },

  // textAlign: 'right', only works for plain text, it doesn't work for div. if you need div right aligned,
  // use RightAlignItem wrapper
  lenderTableThTd: {
    ...Mixins.paddingVertical(12),
    ...Mixins.paddingHorizontal(20),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  lenderTableThTdSmall: {
    ...Mixins.paddingVertical(12),
    ...Mixins.paddingHorizontal(5),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  rightAlignLast: {
    ':last-child': {
      textAlign: 'right',
    },
  },
});

interface CurativeTableProps {
  cellHorizontalPadding?: 'sm' | 'md';
  columnWidths?: number[];
  customEmptyComponent?: React.ReactNode;
  extendedColumnWidths?: number[];
  form: FormApi<FormValues>;
  headerComponents?: React.ReactNode[];
  headerEllipsis?: boolean;
  headerStyle?: React.CSSProperties;
  headerTexts?: string[];
  id?: string;
  isDisabled?: boolean;
  minWidth?: number;
  name: string;
  rightAlignLastCol?: boolean;
  rowStyle?: React.CSSProperties;
  strictWidth?: boolean;
}

export const CurativeTable: React.FC<CurativeTableProps> = ({
  cellHorizontalPadding,
  columnWidths,
  extendedColumnWidths,
  form,
  headerComponents,
  headerStyle,
  headerTexts,
  id,
  isDisabled,
  minWidth,
  name,
  rightAlignLastCol,
  rowStyle,
  strictWidth,
}) => {
  const { order, loanType } = useContext(TitleContext);
  const colWidthsPercentage = columnWidths ? getPercentagesFromWidths(columnWidths, extendedColumnWidths) : [];
  const headerItems = headerComponents || headerTexts;
  const cellStyles = compact([
    cellHorizontalPadding === 'sm' ? styles.lenderTableThTdSmall : styles.lenderTableThTd,
    defaultTo(rightAlignLastCol, true) && styles.rightAlignLast,
    strictWidth && styles.lenderTableStrictWidth,
  ]);

  const handleUploadEventCallback = (rowName: string) => {
    return async (_fileId: string, _file: File, upload: Upload) => {
      const type = name === 'liens' ? 'TITLE:_LIEN_JUDGMENT_SUPPORTING_DOC' : 'TITLE:_TAX_SUPPORTING_DOC';
      if (upload.fileName && upload.url) {
        const { data: docs } = await addDocsToLending({
          files: [{ fileKey: upload.url, fileName: upload.fileName, type }],
          loanId: order!.applicationID,
        });

        if (!isEmpty(docs)) {
          const doc = head(docs);
          const docId = doc!.documentId;
          form.mutators.updateDocId(rowName, docId);
        }
      }
    };
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, rowName: string) => {
    const file = head(e.target.files);
    if (file && order?.applicationID) {
      await uploadSingleFile({
        file,
        handleUploadEvent: handleUploadEventCallback(rowName),
        otherUploadParams: { loanId: order.applicationID },
        uploadFile,
      });
    }
  };

  const renderLienStatusSection = (index: number, loanType?: string) => {
    switch (loanType) {
      case 'HELOAN':
      case 'HELOC':
        return;
      default: {
        return (
          <>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="3"
              style={{ width: colWidthsPercentage[3] }}
            >
              <Field
                data-it="CommitmentCard-LienInputStatus"
                key={`lien-status-${index}`}
                {...lienStatusInputConfig}
                name={`liens[${index}].status`}
              />
            </td>
          </>
        );
      }
    }
  };

  const renderRequirementStatusSection = (index: number, loanType?: string) => {
    switch (loanType) {
      case 'HELOAN':
      case 'HELOC':
        return;
      default:
        return (
          <>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="3"
              style={{ width: colWidthsPercentage[3] }}
            >
              <Field
                data-it="CommitmentCard-RequirementInputStatus"
                key={`requirement-status-${index}`}
                {...requirementStatusInputConfig}
                name={`requirements[${index}].status`}
              />
            </td>
          </>
        );
    }
  };

  const renderUploadsSection = (rowValue: Curative, rowIndex: number, rowName: string, loanType?: string) => {
    switch (loanType) {
      case 'HELOAN':
      case 'HELOC':
        return;
      default:
        return (
          <>
            {!rowValue.docId && (
              <LinkButton
                className={css(styles.curativeAction)}
                id={`${name}-upload-${rowIndex}`}
                inputProps={{
                  accept: '.png,.jpg,.jpeg,.pdf',
                  onChange: async e => {
                    await handleUpload(e, rowName);
                  },
                }}
                key={`${name}-upload-${rowIndex}`}
                label="Upload"
              />
            )}
            {rowValue.docId && (
              <LinkButton
                className={css(styles.curativeAction)}
                id={`${name}-view-upload-${rowIndex}`}
                key={`${name}-view-upload-${rowIndex}`}
                label="View"
                onClick={() => {
                  const enableSecondaryAuth = true;
                  openUrlInNewTab(
                    `/api/internal/loans/${order!.applicationID}/documents/${rowValue.docId!}?inline=true`,
                    enableSecondaryAuth,
                  );
                }}
              />
            )}
          </>
        );
    }
  };

  const generateRowItems = (value: Curative, index: number) => {
    switch (name) {
      case 'liens':
        return (
          <>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="0"
              style={{ width: colWidthsPercentage[0] }}
            >
              <Field
                data-it="CommitmentCard-LienInputCreditorName"
                disabled={isDisabled}
                key={`lien-name-${index}`}
                {...creditorNameInputConfig}
                name={`liens[${index}].creditorName`}
              />
            </td>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="1"
              style={{ width: colWidthsPercentage[1] }}
            >
              <Field
                data-it="CommitmentCard-LienInputType"
                disabled={isDisabled}
                key={`lien-type-${index}`}
                {...lienTypeInputConfig}
                name={`liens[${index}].type`}
              />
            </td>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="2"
              style={{ width: colWidthsPercentage[2] }}
            >
              <Field
                data-it="CommitmentCard-LienInputAmount"
                disabled={isDisabled}
                key={`lien-amount-${index}`}
                {...lienAmountInputConfig}
                name={`liens[${index}].amount`}
              />
            </td>
            {renderLienStatusSection(index, loanType)}
            <td style={{ display: 'none' }}>
              <Field
                data-it="CommitmentCard-LienInputDoc"
                key={`lien-doc-${index}`}
                {...docInputConfig}
                name={`liens[${index}].docId`}
              />
            </td>
          </>
        );
      case 'taxes':
        return (
          <>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="0"
              style={{ width: colWidthsPercentage[0] }}
            >
              <Field
                data-it="CommitmentCard-TaxInputName"
                disabled={isDisabled}
                key={`tax-name-${index}`}
                {...taxNameInputConfig}
                name={`taxes[${index}].name`}
              />
            </td>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="1"
              style={{ width: colWidthsPercentage[1] }}
            >
              <Field
                data-it="CommitmentCard-TaxInputDueDate"
                disabled={isDisabled}
                key={`tax-dueDate-${index}`}
                {...taxDueDateInputConfig}
                name={`taxes[${index}].dueDate`}
              />
            </td>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="2"
              style={{ width: colWidthsPercentage[2] }}
            >
              <Field
                data-it="CommitmentCard-TaxInputAmount"
                disabled={isDisabled}
                key={`tax-amount-${index}`}
                {...taxAmountInputConfig}
                name={`taxes[${index}].amount`}
              />
            </td>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="3"
              style={{ width: colWidthsPercentage[3] }}
            >
              <Field
                data-it="CommitmentCard-TaxInputStatus"
                key={`tax-status-${index}`}
                {...taxStatusInputConfig}
                name={`taxes[${index}].status`}
              />
            </td>
            <td style={{ display: 'none' }}>
              <Field
                data-it="CommitmentCard-TaxInputDoc"
                key={`tax-doc-${index}`}
                {...docInputConfig}
                name={`taxes[${index}].docId`}
              />
            </td>
          </>
        );
      case 'requirements':
        return (
          <>
            <td
              className={css(...cellStyles)}
              data-it={`CurativeTable-column`}
              key="0"
              style={{ width: colWidthsPercentage[0] }}
            >
              <Field
                data-it="CommitmentCard-RequirementInputDescription"
                disabled={isDisabled}
                key={`requirement-desc-${index}`}
                {...requirementDescriptionInputConfig}
                name={`requirements[${index}].description`}
              />
            </td>
            {renderRequirementStatusSection(index, loanType)}
          </>
        );
    }
  };

  const getAddButtonLabel = () => {
    if (name === 'liens') {
      return 'Add Lien / Judgment';
    } else if (name === 'taxes') {
      return 'Add Tax';
    }
    return 'Add Requirement';
  };

  return (
    <FieldArray name={name}>
      {({ fields }) => (
        <>
          <table className={css(styles.lenderTableRoot)} id={id} style={{ minWidth }}>
            {!!headerItems && (
              <thead>
                <tr className={css(styles.lenderTableHeaderTr)} style={headerStyle}>
                  {map(headerItems, (headerTextOrComponent, colIdx) => {
                    return (
                      <th className={css(...cellStyles)} key={colIdx} style={{ width: colWidthsPercentage[colIdx] }}>
                        {headerTextOrComponent}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody>
              {fields.map((rowName, rowIndex) => {
                const rowValue = fields.value[rowIndex] as Curative;
                const rowItems = generateRowItems(rowValue, rowIndex);
                const rowActions = (
                  <td className={css(...cellStyles)}>
                    {name !== 'requirements' && (
                      <LinkButtonWrapper>
                        {renderUploadsSection(rowValue, rowIndex, rowName, loanType)}
                      </LinkButtonWrapper>
                    )}
                    <PopoverList
                      btnSize="md"
                      buttonConfigs={[
                        {
                          label: 'Delete',
                          onClick: () => {
                            fields.remove(rowIndex);
                          },
                        },
                      ]}
                      key={`${name}-action-${rowIndex}`}
                    >
                      <LoneIconButton btnSize="sm" icon="more_horiz" iconSize={22} noBorder theme="tertiary" />
                    </PopoverList>
                  </td>
                );

                return (
                  <tr className={css(styles.lenderTableBodyTr, rowStyle)} data-it="CurativeTable-row" key={rowIndex}>
                    {rowItems}
                    {rowActions}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!isDisabled && (
            <LinkButton
              icon="add"
              id={`CommitmentCard-Add-${name}`}
              label={getAddButtonLabel()}
              onClick={() => fields.push({})}
            />
          )}
        </>
      )}
    </FieldArray>
  );
};
